import React, { useEffect, useState } from 'react';
import { Save, ArrowUp, ArrowDown, Eye, EyeOff, Home as HomeIcon, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import Loader from '@/components/common/Loader';

const SECTION_LABELS = {
  trending: 'Trending Now (product rail)',
  shop_by_room: 'Shop by Room (rooms grid)',
  new_arrivals: 'New Arrivals (product rail)',
  brand_story: 'Brand Story',
  bestsellers: 'Best Sellers (product rail)',
  shop_by_style: 'Shop by Style (style tiles)',
  inspiration: 'Inspiration Gallery',
  editors_picks: "Editor's Picks (product rail)",
  video_banner: 'Video Banner',
  testimonials: 'Testimonials / Reviews',
  instagram: 'Instagram Grid',
};

const FIELD = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none';

export default function AdminHome() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/admin/home').then((r) => setConfig(r.data)).catch(() => toast.error('Could not load homepage config'));
  useEffect(() => { load(); }, []);

  if (!config) return <Loader />;

  const setHero = (patch) => setConfig({ ...config, hero: { ...config.hero, ...patch } });
  const setStory = (patch) => setConfig({ ...config, brand_story: { ...config.brand_story, ...patch } });
  const setVideo = (patch) => setConfig({ ...config, video_banner: { ...config.video_banner, ...patch } });
  const setSection = (idx, patch) => {
    const next = [...config.sections];
    next[idx] = { ...next[idx], ...patch };
    setConfig({ ...config, sections: next });
  };
  const move = (idx, delta) => {
    const next = [...config.sections];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setConfig({ ...config, sections: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/home', {
        hero: config.hero, video_banner: config.video_banner,
        brand_story: config.brand_story, sections: config.sections,
      });
      setConfig(data);
      toast.success('Homepage saved. Refresh the storefront to see it live.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold"><HomeIcon size={20} /> Home Page</h1>
          <p className="text-sm text-muted-foreground">Edit hero, sections and their order. Changes go live after save.</p>
        </div>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold"><Eye size={14} /> View site</a>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold"><RefreshCw size={14} /> Reload</button>
          <button onClick={save} disabled={saving} data-testid="admin-home-save" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-5 py-2 text-xs font-semibold text-white disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      {/* Hero */}
      <section className="mt-5 rounded-2xl border border-border bg-white p-5" data-testid="admin-home-hero">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Hero Section</h2>
          <label className="inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={config.hero.enabled !== false} onChange={(e) => setHero({ enabled: e.target.checked })} /> Enabled</label>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><label className="text-xs font-medium">Heading</label><input value={config.hero.title || ''} onChange={(e) => setHero({ title: e.target.value })} className={FIELD} data-testid="home-hero-title" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-medium">Subtitle</label><textarea rows={2} value={config.hero.subtitle || ''} onChange={(e) => setHero({ subtitle: e.target.value })} className={FIELD} /></div>
          <div><label className="text-xs font-medium">Eyebrow (small text above heading)</label><input value={config.hero.eyebrow || ''} onChange={(e) => setHero({ eyebrow: e.target.value })} className={FIELD} /></div>
          <div><label className="text-xs font-medium">Primary CTA text</label><input value={config.hero.cta || ''} onChange={(e) => setHero({ cta: e.target.value })} className={FIELD} /></div>
          <div><label className="text-xs font-medium">Primary CTA link</label><input value={config.hero.cta_link || ''} onChange={(e) => setHero({ cta_link: e.target.value })} className={FIELD} placeholder="/shop" /></div>
          <div><label className="text-xs font-medium">Secondary CTA text</label><input value={config.hero.secondary_cta || ''} onChange={(e) => setHero({ secondary_cta: e.target.value })} className={FIELD} /></div>
          <div><label className="text-xs font-medium">Secondary CTA link</label><input value={config.hero.secondary_link || ''} onChange={(e) => setHero({ secondary_link: e.target.value })} className={FIELD} placeholder="/rooms" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-medium">Desktop hero image URL</label><input value={config.hero.image || ''} onChange={(e) => setHero({ image: e.target.value })} className={FIELD} data-testid="home-hero-image" placeholder="https://..." /></div>
          <div className="sm:col-span-2"><label className="text-xs font-medium">Mobile hero image URL <span className="text-muted-foreground">(optional)</span></label><input value={config.hero.image_mobile || ''} onChange={(e) => setHero({ image_mobile: e.target.value })} className={FIELD} data-testid="home-hero-image-mobile" placeholder="Leave empty to reuse desktop image" /></div>
        </div>
        {config.hero.image && <img src={config.hero.image_mobile || config.hero.image} alt="hero preview" className="mt-3 aspect-[21/9] w-full rounded-xl object-cover" />}
      </section>

      {/* Brand story */}
      <section className="mt-5 rounded-2xl border border-border bg-white p-5">
        <div className="flex items-center justify-between"><h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Brand Story</h2><label className="inline-flex items-center gap-2 text-xs"><input type="checkbox" checked={config.brand_story.enabled !== false} onChange={(e) => setStory({ enabled: e.target.checked })} /> Enabled</label></div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div><label className="text-xs font-medium">Title</label><input value={config.brand_story.title || ''} onChange={(e) => setStory({ title: e.target.value })} className={FIELD} /></div>
          <div><label className="text-xs font-medium">Image URL</label><input value={config.brand_story.image || ''} onChange={(e) => setStory({ image: e.target.value })} className={FIELD} /></div>
          <div className="sm:col-span-2"><label className="text-xs font-medium">Body text</label><textarea rows={3} value={config.brand_story.text || ''} onChange={(e) => setStory({ text: e.target.value })} className={FIELD} /></div>
        </div>
      </section>

      {/* Video banner */}
      <section className="mt-5 rounded-2xl border border-border bg-white p-5">
        <div className="flex items-center justify-between"><h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Video / Craft Banner</h2></div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div><label className="text-xs font-medium">Title</label><input value={config.video_banner.title || ''} onChange={(e) => setVideo({ title: e.target.value })} className={FIELD} /></div>
          <div><label className="text-xs font-medium">Image URL</label><input value={config.video_banner.image || ''} onChange={(e) => setVideo({ image: e.target.value })} className={FIELD} /></div>
        </div>
      </section>

      {/* Section ordering */}
      <section className="mt-5 rounded-2xl border border-border bg-white p-5" data-testid="admin-home-sections">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Section Order &amp; Visibility</h2>
        <p className="mt-1 text-xs text-muted-foreground">The storefront renders sections in this exact order. Use arrows to move a section up/down.</p>
        <div className="mt-3 space-y-2">
          {config.sections.map((s, idx) => (
            <div key={s.key} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-[hsl(var(--gaze-ivory))] p-3">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-xs font-semibold">{idx + 1}</span>
              <div className="min-w-[180px] flex-1">
                <p className="text-sm font-semibold">{SECTION_LABELS[s.key] || s.key}</p>
                <p className="text-[10px] uppercase text-muted-foreground">{s.key}</p>
              </div>
              {/* Title override */}
              {'title' in s && (
                <input value={s.title || ''} onChange={(e) => setSection(idx, { title: e.target.value })} placeholder="Heading override" className="min-w-[180px] flex-1 rounded-lg border border-border bg-white px-2 py-1.5 text-xs" data-testid={`section-title-${s.key}`} />
              )}
              {'eyebrow' in s && (
                <input value={s.eyebrow || ''} onChange={(e) => setSection(idx, { eyebrow: e.target.value })} placeholder="Eyebrow" className="w-36 rounded-lg border border-border bg-white px-2 py-1.5 text-xs" />
              )}
              {'limit' in s && (
                <div className="flex items-center gap-1"><span className="text-[10px] text-muted-foreground">Show</span><input type="number" min="2" max="20" value={s.limit || 8} onChange={(e) => setSection(idx, { limit: Number(e.target.value) })} className="w-14 rounded-lg border border-border bg-white px-2 py-1.5 text-xs" /></div>
              )}
              <label className="inline-flex items-center gap-1 text-xs">
                <input type="checkbox" checked={s.enabled !== false} onChange={(e) => setSection(idx, { enabled: e.target.checked })} data-testid={`section-enable-${s.key}`} />
                {s.enabled !== false ? <Eye size={13} className="text-emerald-600" /> : <EyeOff size={13} className="text-muted-foreground" />}
              </label>
              <div className="flex gap-1">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="grid h-7 w-7 place-items-center rounded-lg border border-border bg-white disabled:opacity-40" data-testid={`section-up-${s.key}`}><ArrowUp size={13} /></button>
                <button onClick={() => move(idx, +1)} disabled={idx === config.sections.length - 1} className="grid h-7 w-7 place-items-center rounded-lg border border-border bg-white disabled:opacity-40" data-testid={`section-down-${s.key}`}><ArrowDown size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"><Save size={15} /> {saving ? 'Saving…' : 'Save homepage'}</button>
      </div>
    </div>
  );
}
